import { useMemo, useCallback } from "react";
import watermindsLogo from "../../../assets/waterminds-full-logo.png";
import GenericForm from "common/Components/GenericForm/GenericForm";
import AuthPageFooter from "../AuthPageFooter";
import {
  StyledPage,
  StyledAuthMain,
  StyledContainer,
  StyledCard,
  StyledSignupCard,
  StyledSignupCardHeader,
  StyledSignupForm,
  StyledSignupFieldsScroll,
  StyledSignupFormFooter,
  StyledWatermindsLogo,
  StyledTitle,
  StyledSubtitle,
  StyledErrorMessage,
  StyledSubmitButton,
  StyledSuccessMessage,
  SignupNameFieldsRow,
} from "../AuthPageLayout.styles";
import { SIGNUP_UI } from "./signup.constants";
import { useSignup } from "./Signup.hook";

function SignupStatusCard({ title, children }) {
  return (
    <StyledPage>
      <StyledAuthMain>
        <StyledContainer>
          <StyledCard>
            <StyledWatermindsLogo src={watermindsLogo} alt="Waterminds Logo" />
            {title ? (
              <StyledTitle variant="h5" component="h2">
                {title}
              </StyledTitle>
            ) : null}
            {children}
          </StyledCard>
        </StyledContainer>
      </StyledAuthMain>
      <AuthPageFooter />
    </StyledPage>
  );
}

function SignupInviteExpiredCard() {
  return (
    <SignupStatusCard title={SIGNUP_UI.inviteExpiredTitle}>
      <StyledSubtitle component="p">{SIGNUP_UI.inviteExpiredMessage}</StyledSubtitle>
    </SignupStatusCard>
  );
}

function SignupActiveSessionCard({ onLogout, logoutLoading }) {
  return (
    <SignupStatusCard title={SIGNUP_UI.activeSessionTitle}>
      <StyledSubtitle component="p">{SIGNUP_UI.activeSessionMessage}</StyledSubtitle>
      <StyledSubmitButton
        variant="contained"
        onClick={onLogout}
        disabled={logoutLoading}
        sx={{ marginTop: 3 }}
      >
        {logoutLoading ? SIGNUP_UI.loggingOutLabel : SIGNUP_UI.logoutLabel}
      </StyledSubmitButton>
    </SignupStatusCard>
  );
}

export default function Signup() {
  const {
    invite,
    sessionCheck,
    logoutLoading,
    handleLogoutForSignup,
    tokenValidation,
    canShowSignupForm,
    formValues,
    errors,
    bannerError,
    loading,
    availabilityChecking,
    success,
    handleChange,
    handleFieldBlur,
    submit,
  } = useSignup();

  const formDisabled = loading || availabilityChecking;

  const withDisabled = useCallback(
    (field) => ({
      ...field,
      disabled: formDisabled || field.disabled,
    }),
    [formDisabled]
  );

  const signupSections = useMemo(() => {
    const all = SIGNUP_UI.signupFields;
    const pick = (name) => all.find((f) => f.name === name);
    return [
      {
        fields: [withDisabled(pick("email"))],
      },
      {
        wrapper: SignupNameFieldsRow,
        fields: [withDisabled(pick("firstName")), withDisabled(pick("lastName"))],
      },
      {
        fields: [
          withDisabled(pick("password")),
          withDisabled(pick("confirmPassword")),
          withDisabled(pick("phoneNumber")),
          withDisabled(pick("acceptedTerms")),
        ],
      },
    ];
  }, [withDisabled]);

  if (sessionCheck === "loading") {
    return (
      <SignupStatusCard title={SIGNUP_UI.title}>
        <StyledSubtitle>{SIGNUP_UI.sessionCheckMessage}</StyledSubtitle>
      </SignupStatusCard>
    );
  }

  if (sessionCheck === "active") {
    return (
      <SignupActiveSessionCard
        onLogout={handleLogoutForSignup}
        logoutLoading={logoutLoading}
      />
    );
  }

  if (!invite.isValid) {
    return <SignupInviteExpiredCard />;
  }

  const isValidatingInvite =
    tokenValidation.status === "loading" || tokenValidation.status === "idle";

  if (isValidatingInvite) {
    return (
      <SignupStatusCard title={SIGNUP_UI.title}>
        <StyledSubtitle>{SIGNUP_UI.validatingMessage}</StyledSubtitle>
      </SignupStatusCard>
    );
  }

  if (tokenValidation.status === "error") {
    return <SignupInviteExpiredCard />;
  }

  if (
    tokenValidation.status === "ready" &&
    !tokenValidation.requiresSignup
  ) {
    return <SignupInviteExpiredCard />;
  }

  if (success) {
    return (
      <SignupStatusCard title={SIGNUP_UI.title}>
        <StyledSuccessMessage>{SIGNUP_UI.successMessage}</StyledSuccessMessage>
      </SignupStatusCard>
    );
  }

  if (!canShowSignupForm) {
    return null;
  }

  return (
    <StyledPage>
      <StyledAuthMain>
        <StyledContainer>
          <StyledSignupCard>
            <StyledSignupCardHeader>
              <StyledWatermindsLogo src={watermindsLogo} alt="Waterminds Logo" />
              <StyledTitle variant="h5" component="h2">
                {SIGNUP_UI.title}
              </StyledTitle>
              <StyledSubtitle>{SIGNUP_UI.subtitle}</StyledSubtitle>
            </StyledSignupCardHeader>
            <StyledSignupForm noValidate onSubmit={submit}>
              <StyledSignupFieldsScroll>
                <GenericForm
                  sections={signupSections}
                  values={formValues}
                  onChange={handleChange}
                  onFieldBlur={handleFieldBlur}
                  errors={errors}
                  showBorder={false}
                />
              </StyledSignupFieldsScroll>
              <StyledSignupFormFooter>
                {(bannerError || errors.submit) && (
                  <StyledErrorMessage component="p" sx={{ marginTop: 0 }}>
                    {bannerError || errors.submit}
                  </StyledErrorMessage>
                )}
                <StyledSubmitButton
                  type="submit"
                  variant="contained"
                  disabled={formDisabled}
                  fullWidth
                  sx={{ marginTop: bannerError || errors.submit ? 2 : 0 }}
                >
                  {loading ? SIGNUP_UI.submittingLabel : SIGNUP_UI.submitLabel}
                </StyledSubmitButton>
              </StyledSignupFormFooter>
            </StyledSignupForm>
          </StyledSignupCard>
        </StyledContainer>
      </StyledAuthMain>
      <AuthPageFooter />
    </StyledPage>
  );
}
